import { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import type { ChartPoint } from "../lib/api.ts";

// Recharts takes its colours as SVG-attribute props (`stroke`, `fill`),
// not CSS. So `var(...)` doesn't apply — we have to read the computed
// CSS-variable value at render time and re-read on a colour-scheme flip.
// The Tooltip is rendered as a plain DOM div, so we also expose surface
// / fg / border so the popup matches the surrounding theme.
function useThemeColors() {
  const read = () => {
    const cs = getComputedStyle(document.documentElement);
    const get = (name: string, fallback: string) =>
      cs.getPropertyValue(name).trim() || fallback;
    return {
      grid:    get("--c-grid",    "#e2e8f0"),
      muted:   get("--c-muted",   "#64748b"),
      accent:  get("--c-accent",  "#2563eb"),
      danger:  get("--c-danger",  "#dc2626"),
      warning: get("--c-warning", "#d97706"),
      success: get("--c-success", "#16a34a"),
      surface: get("--c-surface", "#ffffff"),
      fg:      get("--c-fg",      "#0f172a"),
      border:  get("--c-border",  "#e2e8f0"),
    };
  };
  const [colors, setColors] = useState(read);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setColors(read());
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return colors;
}

type ThemeColors = ReturnType<typeof useThemeColors>;

// AS1851 testType enum value (mirrors TEST_TYPE_LABELS in lib/api.ts).
const TEST_TYPE_AS1851 = 1;
const TEST_TYPE_CPD    = 3;
const TEST_TYPE_BIR    = 4;
const TEST_TYPE_ECAP   = 6;

// Parse the persisted stop-voltage param (number or numeric string) → number
// or null. Drawn as a dashed reference line on every chart type, matching the
// device chart (genReportChart) and the cloud PDF (reports.ts drawChart).
function stopVoltageOf(params?: Record<string, unknown>): number | null {
  const raw = params?.stop_voltage;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// Nominal voltage band keyed on the pack's start voltage, matching the
// on-device chart (btstatus.cpp genReportChart) and the cloud PDF
// (reports.ts) so all three render the same Y axis — and so the stop-voltage
// line always falls inside the plot, even on a short test that never
// discharged to the cut-off (a data-fitted axis would clip it). `wide`
// (CCD/CPD) lowers the floor so the user-set cut-off (down to 19.2 V on 24 V,
// 9.6 V on 12 V) stays in-plot; AS1851 keeps the standard band.
function voltageBand(
  params: Record<string, unknown> | undefined, rows: ChartPoint[], wide = false,
): { yMin: number; yMax: number; yTicks: number[] } {
  const startVp  = Number(params?.start_voltage);
  const battVals = rows.map((r) => r.v).filter((v): v is number => Number.isFinite(v));
  const startV   = Number.isFinite(startVp) && startVp > 0
    ? startVp
    : (battVals.length > 0 ? Math.max(...battVals) : 12);
  const is24  = startV > 18;
  const yMin  = is24 ? (wide ? 19 : 20) : (wide ? 9 : 10);
  const yMax  = is24 ? 28 : 14;
  const yStep = is24 ? (wide ? 1 : 2) : 1;
  const yTicks: number[] = [];
  for (let v = yMin; v <= yMax + 1e-9; v += yStep) yTicks.push(v);
  return { yMin, yMax, yTicks };
}

export function TestChart({ points, testType, params }: {
  points:    ChartPoint[];
  testType?: number;
  params?:   Record<string, unknown>;
}) {
  const colors = useThemeColors();

  if (points.length === 0) {
    return <p className="muted">No samples in the time-series.</p>;
  }

  if (testType === TEST_TYPE_AS1851) {
    return <As1851ChartView rows={points} params={params} colors={colors} />;
  }
  if (testType === TEST_TYPE_ECAP) {
    // ECAP is current-PRIMARY — its trace is the clamp standby/alarm current in
    // mA (no meaningful voltage series). Dedicated single current axis, matching
    // the on-device report chart (btstatus.cpp genReportChart ECAP branch).
    return <EcapChartView rows={points} colors={colors} />;
  }
  if (testType === TEST_TYPE_CPD || testType === TEST_TYPE_BIR) {
    // BIR is a two-level current discharge; both use the dual-axis (V + I) view.
    return <CpdChartView rows={points} params={params} colors={colors} />;
  }
  // CCD / CRAMP / unknown — voltage-only with dynamic axes.
  return <VoltageChartView rows={points} params={params} colors={colors} />;
}

// -----------------------------------------------------------------------------
// AS1851: 25-minute X axis with one tick per minute, Y range bracketed to
// the nominal pack voltage (12 V → 10–14, 24 V → 20–28). Mirrors the on-
// device chart in btquick/btqml/TestGraph.qml and the in-PDF chart in
// btstatus.cpp::genReportChart so a client comparing screen ↔ PDF ↔ web
// sees the same shape. Stop voltage (params.stop_voltage) is rendered as
// a horizontal reference line if the device persisted it on the test.
// -----------------------------------------------------------------------------
function As1851ChartView({
  rows, params, colors,
}: {
  rows: ChartPoint[];
  params?: Record<string, unknown>;
  colors: ThemeColors;
}) {
  const { yMin, yMax, yTicks } = voltageBand(params, rows);
  const xTicks: number[] = [];
  for (let s = 0; s <= 25 * 60; s += 60) xTicks.push(s);

  const stopV = stopVoltageOf(params);

  return (
    <div className="chart">
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 8, right: 60, bottom: 24, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis
            dataKey="t"
            type="number"
            domain={[0, 25 * 60]}
            ticks={xTicks}
            tickFormatter={(s: number) => `${Math.round(s / 60)}`}
            stroke={colors.muted}
            label={{
              value:    "Time, min",
              position: "insideBottom",
              offset:   -8,
              fill:     colors.muted,
              fontSize: 12,
            }}
          />
          <YAxis
            type="number"
            domain={[yMin, yMax]}
            ticks={yTicks}
            tickFormatter={(v: number) => `${v} V`}
            allowDataOverflow
            stroke={colors.muted}
          />
          <Tooltip content={(p) => renderVoltageTooltip(p, colors)} />
          {stopV != null && (
            <ReferenceLine
              y={stopV}
              stroke={colors.danger}
              strokeDasharray="4 4"
              label={{ value: `Stop ${stopV.toFixed(1)} V`, position: "insideTopLeft", fill: colors.danger, fontSize: 12 }}
            />
          )}
          <Line
            type="monotone"
            dataKey="v"
            stroke={colors.accent}
            name="Vb"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Time-axis unit pick: switch to seconds for sub-2-minute tests. With
// minute formatting, Recharts' auto-ticks for a tiny domain (e.g. 0..11 s)
// all floor to "0" — the user reported "4× 0 labels" symptom from a
// short CCD that tripped on low-voltage in ~10 s.
function pickTimeAxis(tMax: number): {
  ticks:     number[] | undefined;
  format:    (s: number) => string;
  label:     string;
} {
  if (tMax < 120) {
    // < 2 min: seconds, let recharts auto-tick.
    return {
      ticks:  undefined,
      format: (s: number) => `${Math.round(s)}`,
      label:  "Time, s",
    };
  }
  // ≥ 2 min: minute ticks with an ADAPTIVE step (≤ ~12 labels). A fixed
  // one-per-minute step put 900+ explicit ticks on a 15 h capacity test and
  // the axis rendered as a solid band.
  const stepsMin = [1, 2, 5, 10, 15, 30, 60, 120, 240, 480];
  let stepMin = 480;
  for (const st of stepsMin) {
    if (tMax / 60 / st <= 12) { stepMin = st; break; }
  }
  const ticks: number[] = [];
  for (let s = 0; s <= tMax; s += stepMin * 60) ticks.push(s);
  if (tMax >= 3600) {
    // Long runs read as h:mm:ss — "540" minutes for the 9 h mark reads badly.
    const hms = (s: number) => {
      const t   = Math.round(s);
      const h   = Math.floor(t / 3600);
      const m   = Math.floor((t % 3600) / 60);
      const sec = t % 60;
      return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
    };
    return { ticks, format: hms, label: "Time, h:mm:ss" };
  }
  return {
    ticks,
    format: (s: number) => `${Math.round(s / 60)}`,
    label:  "Time, min",
  };
}

// -----------------------------------------------------------------------------
// CPD (Constant Power Discharge): dual Y-axis. Voltage on the left, current
// on the right — power = V·I is held constant on-device, so as voltage
// drops the current rises to compensate. Both curves are the test result.
// Dynamic axes (data-driven) since the test duration isn't fixed at 25 min
// like AS1851.
// -----------------------------------------------------------------------------
function CpdChartView({
  rows, params, colors,
}: {
  rows: ChartPoint[];
  params?: Record<string, unknown>;
  colors: ThemeColors;
}) {
  const tMax = useMemo(() => {
    const ts = rows.map((r) => r.t);
    return ts.length > 0 ? Math.max(...ts) : 0;
  }, [rows]);
  const xAxis = useMemo(() => pickTimeAxis(tMax), [tMax]);
  const stopV = stopVoltageOf(params);
  const { yMin, yMax, yTicks } = voltageBand(params, rows, true);

  return (
    <div className="chart">
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 8, right: 60, bottom: 24, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis
            dataKey="t"
            type="number"
            domain={[0, "dataMax"]}
            ticks={xAxis.ticks}
            tickFormatter={xAxis.format}
            stroke={colors.muted}
            label={{
              value:    xAxis.label,
              position: "insideBottom",
              offset:   -8,
              fill:     colors.muted,
              fontSize: 12,
            }}
          />
          <YAxis
            yAxisId="v"
            type="number"
            domain={[yMin, yMax]}
            ticks={yTicks}
            tickFormatter={(v: number) => `${v} V`}
            allowDataOverflow
            stroke={colors.accent}
          />
          <YAxis
            yAxisId="i"
            orientation="right"
            tickFormatter={(v: number) => `${v.toFixed(0)} A`}
            stroke={colors.danger}
          />
          <Tooltip content={(p) => renderViTooltip(p, colors)} />
          <Legend />
          {stopV != null && (
            <ReferenceLine
              yAxisId="v"
              y={stopV}
              stroke={colors.danger}
              strokeDasharray="4 4"
              label={{ value: `Stop ${stopV.toFixed(1)} V`, position: "insideTopLeft", fill: colors.danger, fontSize: 12 }}
            />
          )}
          <Line
            yAxisId="v"
            type="monotone"
            dataKey="v"
            stroke={colors.accent}
            name="Voltage"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Line
            yAxisId="i"
            type="monotone"
            dataKey="i"
            stroke={colors.danger}
            name="Current"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ECAP (Estimated Capacity): current-primary. The trace is the clamp's standby
// then alarm current, in mA (the currents are sub-amp, so an Amps axis would
// round to 0/1). Single left axis, no voltage. Mirrors the on-device report
// chart (btstatus.cpp genReportChart ECAP branch) and the cloud PDF.
// -----------------------------------------------------------------------------
function EcapChartView({
  rows, colors,
}: {
  rows: ChartPoint[];
  colors: ThemeColors;
}) {
  const tMax = useMemo(() => {
    const ts = rows.map((r) => r.t);
    return ts.length > 0 ? Math.max(...ts) : 0;
  }, [rows]);
  const xAxis = useMemo(() => pickTimeAxis(tMax), [tMax]);
  const data  = useMemo(() => rows.map((r) => ({ t: r.t, mA: (r.i ?? 0) * 1000 })), [rows]);

  return (
    <div className="chart">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 24, bottom: 24, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis
            dataKey="t"
            type="number"
            domain={[0, "dataMax"]}
            ticks={xAxis.ticks}
            tickFormatter={xAxis.format}
            stroke={colors.muted}
            label={{
              value:    xAxis.label,
              position: "insideBottom",
              offset:   -8,
              fill:     colors.muted,
              fontSize: 12,
            }}
          />
          <YAxis
            type="number"
            tickFormatter={(v: number) => `${Math.round(v)} mA`}
            stroke={colors.danger}
          />
          <Tooltip content={(p) => renderCurrentTooltip(p, colors)} />
          <Line
            type="monotone"
            dataKey="mA"
            stroke={colors.danger}
            name="Current"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Default voltage-only view for CCD / CRAMP / BIR and any unknown future test
// type. Dynamic axes; the chart blob carries only voltage for these so there's
// nothing else to plot.
// -----------------------------------------------------------------------------
function VoltageChartView({
  rows, params, colors,
}: {
  rows: ChartPoint[];
  params?: Record<string, unknown>;
  colors: ThemeColors;
}) {
  const tMax = useMemo(() => {
    const ts = rows.map((r) => r.t);
    return ts.length > 0 ? Math.max(...ts) : 0;
  }, [rows]);
  const xAxis = useMemo(() => pickTimeAxis(tMax), [tMax]);
  const stopV = stopVoltageOf(params);
  // CCD / CRAMP / BIR / ECAP — discharge tests, so widen the band like CPD.
  const { yMin, yMax, yTicks } = voltageBand(params, rows, true);

  return (
    <div className="chart">
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 8, right: 60, bottom: 24, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis
            dataKey="t"
            type="number"
            domain={[0, "dataMax"]}
            ticks={xAxis.ticks}
            tickFormatter={xAxis.format}
            stroke={colors.muted}
            label={{
              value:    xAxis.label,
              position: "insideBottom",
              offset:   -8,
              fill:     colors.muted,
              fontSize: 12,
            }}
          />
          <YAxis
            type="number"
            domain={[yMin, yMax]}
            ticks={yTicks}
            tickFormatter={(v: number) => `${v} V`}
            allowDataOverflow
            stroke={colors.muted}
          />
          <Tooltip content={(p) => renderVoltageTooltip(p, colors)} />
          {stopV != null && (
            <ReferenceLine
              y={stopV}
              stroke={colors.danger}
              strokeDasharray="4 4"
              label={{ value: `Stop ${stopV.toFixed(1)} V`, position: "insideTopLeft", fill: colors.danger, fontSize: 12 }}
            />
          )}
          <Line
            type="monotone"
            dataKey="v"
            stroke={colors.accent}
            name="Vb"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tooltip renderers — shared between AS1851 and the default voltage view.
// Aligned two-column grid so the values line up vertically. Theme-coloured
// background/border matches the surrounding card.
// -----------------------------------------------------------------------------
// Recharts ValueType is `string | number | (string|number)[]`, NameType is
// `string | number`. Keep the prop loose and narrow inside each renderer.
type TooltipProps = {
  active?:  boolean;
  payload?: ReadonlyArray<{
    value?:   number | string | Array<number | string>;
    dataKey?: string | number;
  }>;
  label?:   number | string;
};

function tooltipBox(colors: ThemeColors): React.CSSProperties {
  return {
    background:          colors.surface,
    border:              `1px solid ${colors.border}`,
    borderRadius:        6,
    padding:             "0.4rem 0.6rem",
    color:               colors.fg,
    fontSize:            13,
    lineHeight:          1.5,
    display:             "grid",
    gridTemplateColumns: "auto auto",
    columnGap:           "0.5rem",
  };
}

function renderVoltageTooltip(
  { active, payload, label }: TooltipProps,
  colors: ThemeColors,
) {
  if (!active || !payload || payload.length === 0) return null;
  const t   = Math.round(Number(label));
  const raw = payload[0]?.value;
  const v   = typeof raw === "number" ? `${raw.toFixed(2)} V` : String(raw ?? "");
  return (
    <div style={tooltipBox(colors)}>
      <span>T:</span><span>{t} s</span>
      <span>V<sub>b</sub>:</span><span>{v}</span>
    </div>
  );
}

function renderViTooltip(
  { active, payload, label }: TooltipProps,
  colors: ThemeColors,
) {
  if (!active || !payload || payload.length === 0) return null;
  const t  = Math.round(Number(label));
  const vP = payload.find((p) => p.dataKey === "v")?.value;
  const iP = payload.find((p) => p.dataKey === "i")?.value;
  const v  = typeof vP === "number" ? `${vP.toFixed(2)} V` : "—";
  const i  = typeof iP === "number" ? `${iP.toFixed(2)} A` : "—";
  return (
    <div style={tooltipBox(colors)}>
      <span>T:</span><span>{t} s</span>
      <span>V<sub>b</sub>:</span><span>{v}</span>
      <span>I:</span><span>{i}</span>
    </div>
  );
}

// ECAP: single current series in mA (rounded, matching the axis + reports).
function renderCurrentTooltip(
  { active, payload, label }: TooltipProps,
  colors: ThemeColors,
) {
  if (!active || !payload || payload.length === 0) return null;
  const t   = Math.round(Number(label));
  const raw = payload[0]?.value;
  const mA  = typeof raw === "number" ? `${Math.round(raw)} mA` : String(raw ?? "");
  return (
    <div style={tooltipBox(colors)}>
      <span>T:</span><span>{t} s</span>
      <span>I:</span><span>{mA}</span>
    </div>
  );
}
